import { z } from "zod";
import { detailSectionSchema } from "./editor";
import { directRevisionSchema } from "./site-content";

const optionSchema = z.object({ id: z.string(), label: z.string() });
const corporateListItemSchema = z.object({
  id: z.string(), name: z.string(), kind: z.string(), summary: z.string(),
  status: z.string(), meta: z.string(),
});
const teamListItemSchema = z.object({
  id: z.string(), name: z.string(), title: z.string(), bio: z.string(),
  email: z.string(), linkedin: z.string(), image: z.string(), order: z.number(),
  is_active: z.boolean(),
});

export const corporateIndexSchema = z.object({
  partners: z.array(corporateListItemSchema),
  references: z.array(corporateListItemSchema),
  reference_companies: z.array(corporateListItemSchema),
  team: z.array(teamListItemSchema),
});

export const corporateCreateResultSchema = z.object({ id: z.string() }).passthrough();
const baseSchema = {
  id: z.string(),
  locale: z.string(),
  version: z.number(),
  revisions: z.array(directRevisionSchema),
  options: z.object({ sectors: z.array(optionSchema), services: z.array(optionSchema), companies: z.array(optionSchema).optional().default([]) }),
};

export const teamEditorSchema = z.object({
  ...baseSchema,
  kind: z.literal("team"),
  canonical: z.object({
    group: z.enum(["management", "advisory"]).default("advisory"),
    name: z.string(), email: z.string(), linkedin: z.string(), order: z.number(),
    is_active: z.boolean(), media_asset_id: z.string().nullable(), image: z.string(),
  }),
  translation: z.object({ title: z.string(), bio: z.string(), image_alt: z.string(), expertise: z.array(z.string()).default([]) }),
  relations: z.object({}),
  content: z.object({}),
});

const contactSchema = z.object({
  id: z.number().optional(), name: z.string(), email: z.string(), phone: z.string(),
  whatsapp: z.string(), website: z.string(), map_x: z.number().nullable(),
  map_y: z.number().nullable(), order: z.number(), is_active: z.boolean(),
  region: z.string(), city: z.string(), expertise: z.string(), notes: z.string(),
});

export const partnerEditorSchema = z.object({
  ...baseSchema,
  kind: z.literal("partner"),
  canonical: z.object({
    name: z.string(), slug: z.string(), website: z.string(), country_code: z.string(),
    category: z.string(), order: z.number(), is_active: z.boolean(),
    media_asset_id: z.string().nullable(), image: z.string(),
  }),
  translation: z.object({ summary: z.string() }),
  relations: z.object({ sector_id: z.string().nullable() }),
  content: z.object({ contacts: z.array(contactSchema) }),
});

const metricSchema = z.object({
  id: z.number().nullable().optional(), icon: z.string(), order: z.number(), is_active: z.boolean(),
  label: z.string(), value: z.string(), description: z.string(),
});

export const referenceEditorSchema = z.object({
  ...baseSchema,
  kind: z.literal("reference"),
  canonical: z.object({
    source_opportunity_id:z.string().nullable().default(null), show_in_opportunity_pool:z.boolean().default(false),
    is_anonymous: z.boolean().optional().default(false), is_presentation: z.boolean().optional().default(false), organization_id: z.string().nullable(), organization_name: z.string(), year: z.number().nullable(),
    order: z.number(), is_archived: z.boolean(), media_asset_id: z.string().nullable(), image: z.string(),
  }),
  translation: z.object({
    title: z.string(), slug: z.string(), summary: z.string(), location: z.string(),
    image_alt: z.string(), seo_title: z.string(), seo_description: z.string(), is_published: z.boolean(),
  }),
  relations: z.object({ company_id: z.string().nullable().optional().default(null), sector_id: z.string().nullable(), service_ids: z.array(z.string()) }),
  content: z.object({ metrics: z.array(metricSchema), sections: z.array(detailSectionSchema).optional().default([]) }),
});

export const corporateEditorSchema = z.discriminatedUnion("kind", [
  teamEditorSchema,
  partnerEditorSchema,
  referenceEditorSchema,
]);

export type CorporateEditorDto = z.infer<typeof corporateEditorSchema>;
export type CorporateIndexDto = z.infer<typeof corporateIndexSchema>;
